import React, { useState, useEffect, useCallback } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEye,
  faFileUpload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import RecipientCard from "../components/RecipientCard";
import DocumentUpload from "../components/DocumentUpload";
import AddMessage from "../components/AddMessage";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const EditTemplatesPage = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { templateData } = state || {};

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      templateName: "",
      templateDescription: "",
      emailSubject: "",
      emailMessage: "",
      recipients: [],
      document: null,
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "recipients",
  });

  const [isDocumentAccordionOpen, setIsDocumentAccordionOpen] = useState(true);
  const [isRecipientsAccordionOpen, setIsRecipientsAccordionOpen] =
    useState(true);
  const [isMessageAccordionOpen, setIsMessageAccordionOpen] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (templateData) {
      reset({
        templateName: templateData.name,
        templateDescription: templateData.description,
        emailSubject: templateData.email_subject,
        emailMessage: templateData.email_message,
      });

      if (templateData.signers && templateData.signers.length > 0) {
        templateData.signers.forEach((signer) => {
          append({
            name: signer.name,
            email: signer.email,
            role: `Signer ${signer.signing_order}`,
            action: "Needs to Sign",
          });
        });
      }

      if (templateData.document_link) {
        fetch(templateData.document_link)
          .then((res) => res.blob())
          .then((blob) => {
            const file = new File([blob], "document.pdf", {
              type: "application/pdf",
            });
            setUploadedFile(file);
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);
            setValue("document", file);
          })
          .catch((error) => console.error("Error fetching document:", error));
      }
    }
  }, [templateData, reset, append, setValue]);

  const toggleDocumentAccordion = () => {
    setIsDocumentAccordionOpen(!isDocumentAccordionOpen);
  };

  const toggleRecipientsAccordion = () => {
    setIsRecipientsAccordionOpen(!isRecipientsAccordionOpen);
  };

  const toggleMessageAccordion = () => {
    setIsMessageAccordionOpen(!isMessageAccordionOpen);
  };

  const onSubmit = async (data) => {
    try {
      const isValidDocument = await trigger("document");

      if (!isValidDocument) {
        console.log("Document validation failed. Submission prevented.");
        return;
      }

      const recipientsWithOrder = data.recipients.map((recipient, index) => ({
        ...recipient,
        order: index + 1,
      }));

      const dataWithOrder = {
        ...data,
        recipients: recipientsWithOrder,
        document: uploadedFile,
      };

      console.log("Form Data:", dataWithOrder);
      console.log("Uploaded File:", uploadedFile);

      let fileDataURL = null;
      if (uploadedFile) {
        fileDataURL = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(uploadedFile);
        });
      }

      console.log("Data to be updated on the server:", dataWithOrder);

      navigate("/send-template", {
        state: { data: dataWithOrder, fileDataURL: fileDataURL },
      });
    } catch (error) {
      console.error("Error during form submission:", error);
    }
  };

  const handleClear = () => {
    reset();
    setUploadedFile(null);
    setPreviewUrl(null);
    setValue("document", null);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDiscard = () => {
    handleClear();
    navigate("/");
  };

  const handleSaveAndClose = () => {
    handleSubmit(onSubmit)();
    handleCloseModal();
  };
  const handleNext = () => {
    handleSubmit(onSubmit)();
  };

  const onMoveRecipient = useCallback(
    (dragIndex, hoverIndex) => {
      move(dragIndex, hoverIndex);
    },
    [move]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-screen flex flex-col bg-gray-100">
        <div className="sticky top-0 bg-white shadow-md p-4 flex justify-between items-center z-10">
          <h2 className="text-lg font-semibold">Edit Template</h2>
          <div className="flex items-center">
            <button
              data-tooltip-id="closeTooltip"
              onClick={handleOpenModal}
              className="focus:outline-none rounded-full p-2 hover:bg-gray-200 transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
            </button>
            <Tooltip
              id="closeTooltip"
              content="Close"
              place="bottom"
              style={{ zIndex: 10000 }}
            />
          </div>
        </div>

        <div className="container mx-auto p-4 md:p-8 flex-grow">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="max-w-2xl mx-auto space-y-6">
              <div>
                <label
                  htmlFor="templateName"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Template Name
                </label>
                <input
                  type="text"
                  id="templateName"
                  placeholder="Enter template name"
                  {...register("templateName", {
                    required: "Template name is required",
                  })}
                  className="shadow appearance-none ring-1 border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-[#cfc9ff]"
                />
                {errors.templateName && (
                  <p className="text-red-500 text-xs italic">
                    {errors.templateName.message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="templateDescription"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Template Description
                </label>
                <textarea
                  id="templateDescription"
                  placeholder="Description (Optional)"
                  {...register("templateDescription")}
                  className="shadow appearance-none border ring-1 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-[#cfc9ff] h-24"
                />
              </div>
            </div>

            <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
              <button
                type="button"
                className="flex items-center justify-between w-full py-4 px-6 font-semibold text-left text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none"
                onClick={toggleDocumentAccordion}
              >
                <span>Add Document</span>
                <span>{isDocumentAccordionOpen ? "-" : "+"}</span>
              </button>
              {isDocumentAccordionOpen && (
                <div className="p-6">
                  <DocumentUpload
                    uploadedFile={uploadedFile}
                    setUploadedFile={setUploadedFile}
                    previewUrl={previewUrl}
                    setPreviewUrl={setPreviewUrl}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                  />
                </div>
              )}
            </div>

            <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
              <button
                type="button"
                className="flex items-center justify-between w-full py-4 px-6 font-semibold text-left text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none"
                onClick={toggleRecipientsAccordion}
              >
                <span>Add Recipients</span>
                <span>{isRecipientsAccordionOpen ? "-" : "+"}</span>
              </button>
              {isRecipientsAccordionOpen && (
                <div className="p-6">
                  {fields.map((field, index) => (
                    <RecipientCard
                      key={field.id}
                      recipient={field}
                      index={index}
                      remove={remove}
                      register={register}
                      errors={errors}
                      moveRecipient={onMoveRecipient}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
              <button
                type="button"
                className="flex items-center justify-between w-full py-4 px-6 font-semibold text-left text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none"
                onClick={toggleMessageAccordion}
              >
                <span>Add Message</span>
                <span>{isMessageAccordionOpen ? "-" : "+"}</span>
              </button>
              {isMessageAccordionOpen && (
                <div className="p-6">
                  <AddMessage
                    register={register}
                    errors={errors}
                    uploadedFile={uploadedFile}
                    watch={watch}
                  />
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="sticky bottom-0 bg-white shadow-md p-4 flex justify-end items-center z-10">
          {/* <button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            className="mr-2 py-2 px-4 rounded font-semibold border-2 border-[#cfc9ff] text-[#cfc9ff] hover:bg-[#cfc9ff] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#cfc9ff] focus:ring-opacity-50 transition-colors duration-300"
          >
            Save and Close
          </button> */}
          <button
            type="button"
            onClick={handleNext}
            className="bg-indigo-700 hover:bg-[#cfc9ff] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Next
          </button>
        </div>

        {isModalOpen && (
          <div className="fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <div className="flex justify-end">
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <h2 className="text-lg font-semibold mb-4">
                Discard your changes?
              </h2>
              <p className="text-gray-700 mb-4">
                Do you want to discard changes to this template?
              </p>
              <div className="mt-4 flex justify-end space-x-4">
                <button
                  onClick={handleDiscard}
                  className="bg-indigo-700 hover:bg-[#cfc9ff] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Discard Changes
                </button>
                {/* <button
                  onClick={handleSaveAndClose}
                  className="bg-indigo-700 hover:bg-[#cfc9ff] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Save
                </button> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default EditTemplatesPage;
